import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function TarotOverlay({
  open,
  currentTarot,
  tarotOpenedAtRef,
  onClose,
}) {
  if (!open || !currentTarot) return null;

  const { card, orientation } = currentTarot;
  const isReversed = orientation === 'wrong';
  const rotate = isReversed ? '180deg' : '0deg';

  const headerText = isReversed
    ? 'Your fate is reversed.'
    : 'The Tower has spoken.';

  const effectText = card?.orientations?.[orientation]?.description ?? '';

  const tryCloseTarot = (e) => {
    const now =
      typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
        ? performance.now()
        : Date.now();

    if (now - tarotOpenedAtRef.current < 300) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      return;
    }

    e?.stopPropagation?.();
    onClose?.();
  };

  const overlay = (
    <AnimatePresence>
      <motion.div
        key="tarotBackdrop"
        className="fixed inset-0 z-[12000] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        style={{
          backgroundColor: 'rgba(0,0,0,0.65)',
          padding: '16px',
        }}
        onClick={tryCloseTarot}
      >
        <motion.div
          key="tarotCard"
          initial={{ y: 140, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 140, opacity: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="cursor-pointer select-none flex flex-col items-center"
          onClick={(e) => {
            e.stopPropagation();
            tryCloseTarot(e);
          }}
          style={{
            maxWidth: '92vw',
            maxHeight: '88dvh',
          }}
        >
          <div
            className={`mb-3 mt-2 text-center text-3xl sm:text-4xl drop-shadow ${
              isReversed ? 'text-violet-300' : 'text-white/90'
            }`}
            style={{ fontFamily: 'DarkNouveau, ui-sans-serif, system-ui' }}
          >
            {headerText}
          </div>

          <img
            src={card.image}
            alt={card.name}
            draggable={false}
            className="drop-shadow-2xl"
            style={{
              width: 'min(56vw, 200px)',
              height: 'auto',
              maxHeight: '60dvh',
              transform: `rotate(${rotate})`,
            }}
          />

          {effectText && (
            <div className="mt-3 text-center text-white/90 text-lg sm:text-xl leading-snug max-w-[320px] drop-shadow">
              {effectText}
            </div>
          )}

          <div className="mt-1 text-center text-white/60 text-sm drop-shadow">
            Tap to continue
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}